import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Session, Turn } from "../../model_gen/session";
import { Application, Config } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { SessionService } from "../../services/SessionService";
import { AnalyticsService, ApplicationAnalyticsResponse, FaultRateAnalyticsResponse, RequestAnalyticsData, FaultRateAnalyticsData } from "../../services/AnalyticsService";

import SessionOverview from "../Session/SessionOverview";
import { ApplicationAction } from "../../model_gen/application";
import { Model } from "../../model_gen/model";
import { ModelService } from "../../services/ModelService";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { ConfigurationPanelRef } from "../../types/IApplication";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import OverflowTabs, { TabItem } from "../ui/overflow-tabs";
import { 
  SettingsIcon,
  AppWindowIcon,
  HistoryIcon,
  FileTextIcon,
  CalendarIcon,
  ServerIcon,
  RotateCcwIcon
} from "lucide-react";
import IntentsTable from "./IntentsTable";
import SessionsTable from "./SessionsTable";
import ModelsTable from "../shared/ModelsTable";
import SmartPagination from "../shared/SmartPagination";
import moment from "moment";
import ReconnectingWebSocket from "reconnecting-websocket";
import RequestAnalyticsChart from "../shared/RequestAnalyticsChart";
import FaultAnalyticsChart from "../shared/FaultAnalyticsChart";
import { QuickPeriod } from "../shared/AnalyticsDateFilter";

interface SortConfig {
  key: keyof Session | null;
  direction: "asc" | "desc";
}

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const ApplicationOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState<Partial<Application> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sessionsByPage, setSessionsByPage] = useState<Record<number, Session[]>>({});
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [originalConfig, setOriginalConfig] = useState<Config>();
  const [intents, setIntents] = useState<ApplicationAction[]>([]);
  const [intentsLoading, setIntentsLoading] = useState<boolean>(false);
  const [models, setModels] = useState<Model[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(false);
  const [intentSortConfig, setIntentSortConfig] = useState<{ key: keyof ApplicationAction | null; direction: "asc" | "desc" }>({ key: null, direction: "desc" });
  
  // Add pagination state for intents and models
  const [intentCurrentPage, setIntentCurrentPage] = useState(1);
  const [modelCurrentPage, setModelCurrentPage] = useState(1);
  const [totalIntents, setTotalIntents] = useState(0);
  const [totalModels, setTotalModels] = useState(0);
  const [intentsByPage, setIntentsByPage] = useState<Record<number, ApplicationAction[]>>({});
  
  const pageSize = 10;
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const lastMessageTimeRef = useRef<Date>(new Date());
  
  // Configuration save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const configPanelRef = useRef<ConfigurationPanelRef>(null);

  // Analytics state
  const [requestAnalyticsData, setRequestAnalyticsData] = useState<RequestAnalyticsData[]>([]);
  const [requestAnalyticsLoading, setRequestAnalyticsLoading] = useState<boolean>(false);
  const [requestAnalyticsPeriod, setRequestAnalyticsPeriod] = useState<QuickPeriod>({ label: "1W", days: 7 });
  const [requestAnalyticsPeriodDays, setRequestAnalyticsPeriodDays] = useState<number>(7);

  const [faultAnalyticsData, setFaultAnalyticsData] = useState<FaultRateAnalyticsData[]>([]);
  const [faultAnalyticsLoading, setFaultAnalyticsLoading] = useState<boolean>(false);
  const [faultAnalyticsPeriod, setFaultAnalyticsPeriod] = useState<QuickPeriod>({ label: "1W", days: 7 });
  const [faultAnalyticsPeriodDays, setFaultAnalyticsPeriodDays] = useState<number>(7);

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    let newConfig: Partial<Config> = application?.meta;
    if (!newConfig) {
      newConfig = config;
    }
    if (!application) {
      return;
    }
    const newApplication = {
      ...application,
      meta: {
        ...newConfig,
        ...config,
      },
    };
    setApplication(newApplication);
    if (!save) {
      return;
    }
    ApplicationService.setApplicationConfig(
      application.id,
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (updatedConfig: Config) => {
        setApplication((prevState) => {
          if (!prevState) return prevState;
          return {
            ...prevState,
            meta: updatedConfig,
          };
        });
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const resetConfig = () => {
    if (originalConfig) {
      updateConfig(originalConfig, () => {});
    }
  };

  const handleSaveStateChange = (hasUnsaved: boolean, saving: boolean) => {
    setHasUnsavedChanges(hasUnsaved);
    setIsSaving(saving);
  };

  const handleSaveChanges = () => {
    if (configPanelRef.current) {
      configPanelRef.current.saveChanges();
    }
  };

  // Update total sessions from application metadata
  const updateTotalSessionsFromMetadata = useCallback(() => {
    if (application?.meta?.metadata?.session_count !== undefined) {
      setTotalSessions(Number(application.meta.metadata.session_count) || 0);
    }
  }, [application]);

  useEffect(() => {
    if (applicationId) {
      ApplicationService.getApplication(
        Number(applicationId),
        setIsLoading,
        apiKey
      ).subscribe(
        (app: Application) => {
          setApplication(app);
          setOriginalConfig(app.meta);

          // Set total sessions from metadata
          if (app.meta?.metadata?.session_count !== undefined) {
            setTotalSessions(Number(app.meta.metadata.session_count) || 0);
          }
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
    return openWebsocket();
  }, [applicationId]);

  // Update total sessions when application metadata changes
  useEffect(() => {
    updateTotalSessionsFromMetadata();
  }, [updateTotalSessionsFromMetadata]);

  const loadSessions = useCallback(() => {
    if (!applicationId || activeTab !== "sessions") return;

    if (!sessionsByPage[currentPage]) {
      setSessionsLoading(true);
      SessionService.getApplicationSessions(
        applicationId,
        (currentPage - 1) * pageSize,
        pageSize,
        setSessionsLoading,
        apiKey
      ).subscribe(
        ({ sessions, total }: { sessions: Session[]; total: number }) => {
          const processedSessions = sessions.map((session) => ({
            ...session,
            timeline: session.timeline || [],
            cost: session.cost ?? -1,
          }));

          setSessions(processedSessions);
          // Update total sessions from API response as a fallback
          if (
            total &&
            (!application?.meta?.metadata?.session_count ||
              total > Number(application.meta.metadata.session_count))
          ) {
            setTotalSessions(total);
          }
          setSessionsByPage((prev) => ({
            ...prev,
            [currentPage]: processedSessions,
          }));
        },
        (error: any) => {
          console.error(error);
        }
      );
    } else {
      setSessions(sessionsByPage[currentPage]);
    }
  }, [
    applicationId,
    currentPage,
    pageSize,
    apiKey,
    sessionsByPage,
    activeTab,
    application,
  ]);

  useEffect(() => {
    if (applicationId) {
      loadSessions();
    }
  }, [loadSessions, refreshTrigger, activeTab]);

  const openWebsocket = () => {
    closeWsConnection();

    const ws: any = new ReconnectingWebSocket(
      `wss://09hidyy627.execute-api.us-west-2.amazonaws.com/production?key=${applicationId}&type=NEW_SESSION,SESSION_UPDATE`
    );
    wsRef.current = ws;

    ws.addEventListener("message", (message: any) => {
      lastMessageTimeRef.current = new Date();
      const data = JSON.parse(message.data);
      switch (data.event_type) {
        case "NEW_SESSION":
          handleNewSession(data);
          break;
        case "SESSION_UPDATE":
          handleNewSessionTurn(data);
          break;
      }
    });

    // create an interval that checks the last message time and refreshes connection if needed
    const checkInterval = setInterval(() => {
      const now = new Date();
      // Check the payments WS
      const diffInMinutesWS =
        (now.getTime() - lastMessageTimeRef.current.getTime()) / (1000 * 60);

      if (diffInMinutesWS >= 1) {
        // adjust time as needed
        if (wsRef.current) {
          wsRef.current.reconnect(1000, "Manual reconnect");
          lastMessageTimeRef.current = new Date();
        }
      }
    }, 1000 * 5); // check every 5 seconds

    console.log("Opened WS connection");

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      closeWsConnection();
    };
  };

  const closeWsConnection = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      console.log("Closed WS connection");
    }
  };

  const handleNewSession = (data: any) => {
    const newSession: Session = JSON.parse(data.event_data);
    if (newSession.timeline == undefined) {
      newSession.timeline = [];
    }
    if (!newSession.cost) {
      newSession.cost = -1;
    }
    const sessionId = newSession.session_id;

    // Set the date_created to the current time in seconds
    newSession.date_created = Date.now();

    setSessions((prevSessions) => {
      const updatedSessions = [...prevSessions];
      const sessionExists = updatedSessions?.some(
        (session) => session.session_id === sessionId
      );

      if (!sessionExists && currentPage == 1) {
        updatedSessions.unshift(newSession); // Prepend the new session to the beginning of the sessions array
      }

      return updatedSessions;
    });

    // Update total session count
    setTotalSessions((prev) => prev + 1);
  };

  const handleNewSessionTurn = (data: any) => {
    const turn: Turn = JSON.parse(data.event_data);
    const sessionId = turn.request?.session_id || turn.eval_request?.session_id;

    if (sessionId) {
      setSessions((prevSessions) => {
        const updatedSessions = [...prevSessions];
        const sessionIndex = updatedSessions.findIndex(
          (session) => session.session_id === sessionId
        );

        if (sessionIndex !== -1) {
          const session = updatedSessions[sessionIndex];
          const turnIndex = session.timeline.findIndex(
            (t) =>
              (t.request?.request_id &&
                turn.request?.request_id &&
                t.request.request_id === turn.request.request_id) ||
              (t.response?.request_id &&
                turn.response?.request_id &&
                t.response.request_id === turn.response.request_id) ||
              (t.eval_request?.request_id &&
                turn.eval_request?.request_id &&
                t.eval_request.request_id === turn.eval_request.request_id)
          );
          if (turnIndex !== -1) {
            const existingTurn = session.timeline[turnIndex];
            session.timeline[turnIndex] = {
              ...existingTurn,
            };
            if (turn.request?.request_id) {
              session.timeline[turnIndex].request = turn.request;
            }
            if (turn.response?.request_id) {
              session.timeline[turnIndex].response = turn.response;
            }
            if (turn.eval_request?.request_id) {
              session.timeline[turnIndex].eval_request = turn.eval_request;
            }
          } else if (turn.request || turn.response) {
            session.timeline.push(turn);
          }

          updatedSessions[sessionIndex] = session;
        }

        return updatedSessions;
      });
    }
  };

  const handleSort = (key: keyof Session) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedSessions = React.useMemo(() => {
    const sortableSessions = [...sessions];
    if (sortConfig.key) {
      sortableSessions.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Default sort by date_created descending
      sortableSessions.sort((a, b) => {
        const dateA = a.date_created > 0 ? a.date_created : 0;
        const dateB = b.date_created > 0 ? b.date_created : 0;
        return dateB - dateA;
      });
    }
    return sortableSessions;
  }, [sessions, sortConfig]);

  const handleSessionClick = (session: Session) => {
    setSelectedSessionId(session.session_id);
    setSessionModalVisible(true);
    setRefreshTrigger((prev) => prev + 1);
  };





  // Load intents
  const loadIntents = useCallback(() => {
    if (!applicationId || activeTab !== "intents") return;

    if (!intentsByPage[intentCurrentPage]) {
      setIntentsLoading(true);
      ApplicationService.getApplicationActions(
        Number(applicationId),
        setIntentsLoading,
        apiKey
      ).subscribe(
        (actions: ApplicationAction[]) => {
          // Sort actions by last_activity in descending order
          const sortedActions = actions.sort((a, b) => {
            const dateA = a.last_activity > 0 ? a.last_activity : 0;
            const dateB = b.last_activity > 0 ? b.last_activity : 0;
            return dateB - dateA;
          });
          
          setTotalIntents(sortedActions.length);
          
          // Paginate the results
          const startIndex = (intentCurrentPage - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedActions = sortedActions.slice(startIndex, endIndex);
          
          setIntents(paginatedActions);
          setIntentsByPage((prev) => ({
            ...prev,
            [intentCurrentPage]: paginatedActions,
          }));
        },
        (error: any) => {
          console.error(error);
          setIntentsLoading(false);
        }
      );
    } else {
      // Even for cached data, show loading briefly for better UX
      setIntentsLoading(true);
      // Use setTimeout to simulate brief loading state
      setTimeout(() => {
        setIntents(intentsByPage[intentCurrentPage]);
        setIntentsLoading(false);
      }, 100);
    }
  }, [applicationId, activeTab, apiKey, intentCurrentPage, intentsByPage, pageSize]);

  // Load models
  const loadModels = useCallback(() => {
    if (!applicationId || activeTab !== "models") return;

    setModelsLoading(true);
    ModelService.getModelsByApplication(
      Number(applicationId),
      setModelsLoading,
      apiKey
    ).subscribe(
      (data: Model[]) => {
        // Filter to only include finetuned models
        const finetunedModels = data.filter((model) => model.finetuned);
        
        setTotalModels(finetunedModels.length);
        
        // Client-side pagination
        const startIndex = (modelCurrentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedModels = finetunedModels.slice(startIndex, endIndex);
        
        setModels(paginatedModels);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setModelsLoading(false);
        setModels([]);
      }
    );
  }, [applicationId, activeTab, apiKey, modelCurrentPage, pageSize]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === "intents") {
      setIntentCurrentPage(1); // Reset to first page when switching tabs
      // loadIntents will be triggered by the intentCurrentPage useEffect below
    } else if (activeTab === "models") {
      setModelCurrentPage(1); // Reset to first page when switching tabs
      // loadModels will be triggered by the modelCurrentPage useEffect below
    }
  }, [activeTab]);

  // Load intents when page changes
  useEffect(() => {
    if (activeTab === "intents") {
      loadIntents();
    }
  }, [intentCurrentPage, loadIntents, activeTab]);

  // Load models when page changes
  useEffect(() => {
    if (activeTab === "models") {
      loadModels();
    }
  }, [modelCurrentPage, loadModels, activeTab]);



  const handleIntentSort = (key: keyof ApplicationAction) => {
    let direction: "asc" | "desc" = "asc";
    if (intentSortConfig.key === key && intentSortConfig.direction === "asc") {
      direction = "desc";
    }
    setIntentSortConfig({ key, direction });
  };



  const sortedIntents = React.useMemo(() => {
    const sortableIntents = [...intents];
    if (intentSortConfig.key) {
      sortableIntents.sort((a, b) => {
        const aValue = a[intentSortConfig.key!];
        const bValue = b[intentSortConfig.key!];
        
        if (aValue < bValue) {
          return intentSortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return intentSortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableIntents;
  }, [intents, intentSortConfig]);



  const getSessionCount = () => {
    if (application?.meta?.metadata?.session_count !== undefined) {
      return Number(application.meta.metadata.session_count) || 0;
    }
    return totalSessions;
  };

  // Analytics fetch functions
  const fetchRequestAnalytics = useCallback((period: QuickPeriod) => {
    if (!applicationId) return;

    setRequestAnalyticsLoading(true);
    
    const now = moment();
    const days = period.days;
    
    let startDate: string;
    let endDate: string;
    
    if (days === 1) {
      // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
      startDate = now.clone().subtract(24, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    } else {
      // For multi-day periods, use the same logic as 1D but with more hours
      const totalHours = days * 24;
      startDate = now.clone().subtract(totalHours, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    }
    
    console.log(`📅 Request analytics period: ${days} days, ${startDate} to ${endDate}`);
    setRequestAnalyticsPeriodDays(days);

    AnalyticsService.getApplicationRequestAnalytics(
      Number(applicationId),
      setRequestAnalyticsLoading,
      apiKey,
      startDate,
      endDate
    ).subscribe(
      (response: ApplicationAnalyticsResponse) => {
        console.log("📊 Request analytics response:", response);
        setRequestAnalyticsData(response.data || []);
      },
      (error: any) => {
        console.error("Error fetching request analytics:", error);
        setRequestAnalyticsData([]);
      }
    );
  }, [applicationId, apiKey]);

  const fetchFaultAnalytics = useCallback((period: QuickPeriod) => {
    if (!applicationId) return;

    setFaultAnalyticsLoading(true);
    
    const now = moment();
    const days = period.days;
    
    let startDate: string;
    let endDate: string;
    
    if (days === 1) {
      // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
      startDate = now.clone().subtract(24, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    } else {
      // For multi-day periods, use the same logic as 1D but with more hours
      const totalHours = days * 24;
      startDate = now.clone().subtract(totalHours, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    }
    
    console.log(`📅 Fault analytics period: ${days} days, ${startDate} to ${endDate}`);
    setFaultAnalyticsPeriodDays(days);

    AnalyticsService.getApplicationFaultRateAnalytics(
      Number(applicationId),
      setFaultAnalyticsLoading,
      apiKey,
      startDate,
      endDate
    ).subscribe(
      (response: FaultRateAnalyticsResponse) => {
        console.log("📊 Fault analytics response:", response);
        setFaultAnalyticsData(response.data || []);
      },
      (error: any) => {
        console.error("Error fetching fault analytics:", error);
        setFaultAnalyticsData([]);
      }
    );
  }, [applicationId, apiKey]);

  const handleRequestAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setRequestAnalyticsPeriod(period);
    fetchRequestAnalytics(period);
  };

  const handleFaultAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setFaultAnalyticsPeriod(period);
    fetchFaultAnalytics(period);
  };

  // Load request analytics when overview tab becomes active or period changes
  useEffect(() => {
    if (activeTab === "overview") {
      fetchRequestAnalytics(requestAnalyticsPeriod);
    }
  }, [activeTab, requestAnalyticsPeriod, fetchRequestAnalytics]);

  // Load fault analytics when overview tab becomes active or period changes
  useEffect(() => {
    if (activeTab === "overview") {
      fetchFaultAnalytics(faultAnalyticsPeriod);
    }
  }, [activeTab, faultAnalyticsPeriod, fetchFaultAnalytics]);

  // Load intents count for overview tab
  useEffect(() => {
    if (activeTab === "overview" && applicationId) {
      setIntentsLoading(true);
      ApplicationService.getApplicationActions(
        Number(applicationId),
        setIntentsLoading,
        apiKey
      ).subscribe(
        (actions: ApplicationAction[]) => {
          setTotalIntents(actions.length);
        },
        (error: any) => {
          console.error("Error fetching intents count for overview:", error);
          setTotalIntents(0);
        }
      );
    }
  }, [activeTab, applicationId, apiKey]);











  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: (
                 <div className="space-y-8">
           {/* Key Metrics */}
           <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
             <Card className="border-border">
               <CardContent className="p-6">
                 <div className="space-y-2">
                   <div className="flex items-center gap-2">
                     <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                     <p className="text-sm text-muted-foreground font-gc-reg">Total Sessions</p>
                   </div>
                   <p className="text-lg font-gc-med text-foreground">{getSessionCount()}</p>
                 </div>
               </CardContent>
             </Card>
             
             <Card className="border-border">
               <CardContent className="p-6">
                 <div className="space-y-2">
                   <div className="flex items-center gap-2">
                     <AppWindowIcon className="w-4 h-4 text-muted-foreground" />
                     <p className="text-sm text-muted-foreground font-gc-reg">Intents</p>
                   </div>
                   <p className="text-lg font-gc-med text-foreground">
                     {totalIntents}
                   </p>
                 </div>
               </CardContent>
             </Card>
             
                         <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Last Activity</p>
                  </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {application?.last_activity && application.last_activity > 0 
                      ? moment(application.last_activity).fromNow()
                      : "Never"
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
             
             <Card className="border-border">
               <CardContent className="p-6">
                 <div className="space-y-2">
                   <div className="flex items-center gap-2">
                     <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                     <p className="text-sm text-muted-foreground font-gc-reg">Created</p>
                   </div>
                   <p className="text-lg font-gc-med text-foreground">
                     {moment(application?.date_created).format("MMM D, YYYY")}
                   </p>
                 </div>
               </CardContent>
             </Card>
          </div>

          {/* Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RequestAnalyticsChart
              data={requestAnalyticsData}
              loading={requestAnalyticsLoading}
              title="Request Activity"
              onQuickPeriodSelect={handleRequestAnalyticsPeriodSelect}
              selectedPeriod={requestAnalyticsPeriod}
              periodDays={requestAnalyticsPeriodDays}
            />
            <FaultAnalyticsChart
              data={faultAnalyticsData}
              loading={faultAnalyticsLoading}
              title="Fault Rate"
              onQuickPeriodSelect={handleFaultAnalyticsPeriodSelect}
              selectedPeriod={faultAnalyticsPeriod}
              periodDays={faultAnalyticsPeriodDays}
            />
          </div>
        </div>
      )
    },
    {
      key: "intents",
      label: "Intents",
      icon: <FileTextIcon className="w-4 h-4" />,
      content: (
                <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Intents</h3>
            <p className="text-sm text-muted-foreground">Manage application intents and their configurations</p>
          </div>
          
          <IntentsTable
            intents={sortedIntents}
            loading={intentsLoading}
            sortConfig={intentSortConfig}
            onSort={handleIntentSort}
            onIntentClick={(intent) => navigate(`/application/${intent.application_id}/intent/${intent.id}`)}
            onRefresh={() => {
              setIntentsByPage({});
              loadIntents();
            }}
            apiKey={apiKey}
            setIsLoading={setIntentsLoading}
          />

          {/* Pagination for Intents */}
          {sortedIntents.length > 0 && (
            <SmartPagination
              currentPage={intentCurrentPage}
              totalPages={Math.ceil(totalIntents / pageSize)}
              onPageChange={setIntentCurrentPage}
              itemName="intents"
              totalItems={totalIntents}
              itemsPerPage={pageSize}
            />
          )}
        </div>
      )
    },
    {
      key: "sessions",
      label: "Sessions",
      icon: <HistoryIcon className="w-4 h-4" />,
      content: (
        <SessionsTable
          sessions={sortedSessions}
          loading={sessionsLoading}
          sortConfig={sortConfig}
          onSort={handleSort}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          totalSessions={getSessionCount()}
          onSessionClick={handleSessionClick}
        />
      )
    },
         {
       key: "models",
       label: "Models",
       icon: <ServerIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Models</h3>
            <p className="text-sm text-muted-foreground">Manage application models and configurations</p>
          </div>
          
          <ModelsTable
            models={models}
            loading={modelsLoading}
            currentPage={modelCurrentPage}
            totalPages={Math.ceil(totalModels / pageSize)}
            totalItems={totalModels}
            itemsPerPage={pageSize}
            onPageChange={setModelCurrentPage}
            emptyTitle="No fine-tuned models found"
            emptyDescription="No fine-tuned models have been created for this application yet."
          />
        </div>
      )
    },
    {
      key: "configuration",
      label: "Configuration",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Configuration</h3>
              <p className="text-sm text-muted-foreground">Manage application settings and parameters</p>
            </div>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <span className="text-sm text-muted-foreground">
                  You have unsaved changes
                </span>
              )}
              <Button
                variant="outline"
                onClick={resetConfig}
                className="gap-2"
              >
                <RotateCcwIcon className="w-4 h-4" />
                Reset to Default
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges || isSaving}
                className="gap-2"
              >
                {isSaving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
          <ConfigurationPanel
            ref={configPanelRef}
            config={application?.meta}
            updateConfig={updateConfig}
            resetConfig={resetConfig}
            showSystemPrompt={false}
            onSaveStateChange={handleSaveStateChange}
          />
        </div>
      )
    }
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map(tab => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/applications")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Applications
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">{application?.application_name}</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                {application?.application_name}
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage intents, monitor sessions, and configure application settings
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <OverflowTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find(tab => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Session Details Modal */}
      <Dialog open={sessionModalVisible} onOpenChange={setSessionModalVisible}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <HistoryIcon className="w-5 h-5 text-maitai-green" />
              Session Details
            </DialogTitle>
          </DialogHeader>
          {selectedSessionId && applicationId && (
            <SessionOverview
              sessionId={selectedSessionId}
              applicationId={applicationId}
              refreshTrigger={refreshTrigger}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationOverview;
